import { useEffect, useRef, useState } from "react";
import { ChevronDown, LogOut, Monitor, Moon, Settings, Shield, Sun } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { firstAdminPath, hasAdminAccess } from "../../config/navigation";
import { useAuth } from "../../stores/auth";
import { useShellTabs } from "../../stores/shell-tabs";
import { getTheme, setTheme, type ThemeMode } from "../../stores/theme";

export function UserMenu() {
  const navigate = useNavigate();
  const { user, can, logout } = useAuth();
  const openInActiveTab = useShellTabs((s) => s.openInActiveTab);
  const [open, setOpen] = useState(false);
  const [theme, setThemeState] = useState<ThemeMode>(() => getTheme());
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const showAdmin = hasAdminAccess(can);
  const adminPath = firstAdminPath(can);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-hover"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-brand text-xs font-bold">
          {(user?.displayName ?? "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="hidden min-w-0 sm:block">
          <p className="truncate text-xs font-semibold text-foreground">{user?.displayName ?? "Staff"}</p>
          <p className="truncate text-[10px] text-muted">{user?.roles[0] ?? "Admin"}</p>
        </div>
        <ChevronDown size={14} className="text-muted-2" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-[10040] min-w-[220px] overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-xl"
        >
          <div className="border-b border-line px-3 py-2">
            <p className="truncate text-xs font-medium text-foreground">{user?.displayName}</p>
            <p className="truncate text-[10px] text-muted">{user?.email}</p>
          </div>

          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-hover"
            onClick={() => {
              const next: ThemeMode = theme === "dark" ? "light" : "dark";
              setTheme(next);
              setThemeState(next);
            }}
          >
            {theme === "dark" ? (
              <Sun size={16} className="text-muted" />
            ) : (
              <Moon size={16} className="text-muted" />
            )}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </button>

          {showAdmin && adminPath && (
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-hover"
              onClick={() => {
                const path = openInActiveTab(adminPath);
                navigate(path);
                setOpen(false);
              }}
            >
              <Settings size={16} className="text-muted" />
              Admin Configuration
            </button>
          )}

          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-hover"
            onClick={() => {
              navigate("/profile/security");
              setOpen(false);
            }}
          >
            <Shield size={16} className="text-muted" />
            Security
          </button>

          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-hover"
            onClick={() => {
              navigate("/os");
              setOpen(false);
            }}
          >
            <Monitor size={16} className="text-muted" />
            Enter OS mode
          </button>

          <div className="my-1 border-t border-line" />

          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-hover"
            onClick={() => {
              void logout().then(() => navigate("/login"));
              setOpen(false);
            }}
          >
            <LogOut size={16} className="text-muted" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
